import { useEffect, useRef, useState } from 'react';
import { useObservable } from '@paradigm/utils/src/hooks';
import { useDrfqDraftsStore } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import useCreateMarket from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarket';
import { wasDraftWsDelivered } from '@paradigm/features/src/unified-rfqs/store/agent-draft-arrivals';

import {
  getDraftHandoffToken,
  matchesDraftHandoffToken,
} from '#/features/terminal/draft-handoff';
import { TOAST_TIMEOUT_LONG, toast } from '#/components/UnifiedRfqs/toast';
import { claimDraftOnce, releaseDraftClaim } from './claim-draft-once';

const onError = (message: string) =>
  toast(message, { autoClose: TOAST_TIMEOUT_LONG });

// ============================================================
// TEMP (revert before merge) — manual test hook while the Dime
// terminal connection is down. Create a draft RFQ over the API with
// label "dime:test-draft-handoff" and the builder will open, exercising
// the WS + opener path without the terminal injecting the real token.
// Accepted IN ADDITION to the per-window generated token, so the real
// terminal path is unaffected.
const TEMP_TEST_DRAFT_TOKEN = 'test-draft-handoff';
// ============================================================

function useIsDocumentVisible() {
  const [visible, setVisible] = useState(
    () => globalThis.document?.visibilityState !== 'hidden',
  );
  useEffect(() => {
    const doc = globalThis.document;
    if (doc == null) return undefined;
    const onChange = () => setVisible(doc.visibilityState !== 'hidden');
    doc.addEventListener('visibilitychange', onChange);
    return () => doc.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * Opens the RFQ Builder for a draft RFQ the Dime terminal agent just
 * created for this app instance.
 *
 * The DRFQ WebSocket handler records agent drafts (taker desk only) as
 * live-WS arrivals and lands them in the drafts store; this observer
 * watches that store and opens the builder for a draft whose `label`
 * carries THIS instance's handoff token. That token scoping is what keeps
 * the builder from popping for other members of the same desk (the
 * backend can only address the desk, not the user) and for other tabs
 * (each mints its own token).
 *
 * Guard ordering matters. Only *permanent*, draft-intrinsic rejections
 * (wrong token, not a DRAFT) are memoized in `seenIds`. The *transient*
 * gates are NOT memoized, so a later store emission re-evaluates them:
 * the visibility gate (hidden window → early return, re-runs on refocus,
 * the common path) and "not a live-WS arrival yet" (the REST rehydrate
 * can emit a draft id before `DRAFT_ADDED` records its arrival). At most
 * one draft opens per pass — the builder modal is single-slot — so the
 * rest are left for the next emission. `claimDraftOnce` opens each draft
 * exactly once across tabs/reloads, and is released if the open fails so
 * a later attempt can retry.
 *
 * Renders nothing. Must be mounted inside the RFQ builder + DRFQ
 * dependency providers. Inert when the terminal is off (no token is ever
 * stamped, so nothing matches).
 */
export default function AgentDraftRfqOpener() {
  const draftsStore = useDrfqDraftsStore();
  const drafts = useObservable(draftsStore.list());
  const { createMarket } = useCreateMarket(true);
  const seenIds = useRef<Set<string>>(new Set());
  const isVisible = useIsDocumentVisible();

  useEffect(() => {
    // Don't pop a modal in a backgrounded window; the effect re-runs when
    // `isVisible` flips back to true and processes anything that arrived
    // while hidden.
    if (!isVisible) return;

    // TEMP: accept the generated token OR the hardcoded test token.
    // Revert to `const token = getDraftHandoffToken();` + a single
    // matchesDraftHandoffToken(draft.label, token) check before merge.
    const tokens = [getDraftHandoffToken(), TEMP_TEST_DRAFT_TOKEN];

    // Open at most one draft per pass. `createMarket` resolves the draft
    // synchronously and calls the single-slot builder modal, so opening
    // two in one emission would overwrite the first's prefill while
    // claiming both — burning the first. `find` short-circuits at the
    // first eligible draft; the rest stay unclaimed for the next emission.
    const next = drafts.find((draft) => {
      const id = String(draft.id);
      if (seenIds.current.has(id)) return false;

      // Permanent, draft-intrinsic rejections — safe to memoize so we
      // don't re-check them on every store emission.
      if (!tokens.some((t) => matchesDraftHandoffToken(draft.label, t))) {
        seenIds.current.add(id);
        return false;
      }
      if (draft.state !== 'DRAFT') {
        seenIds.current.add(id);
        return false;
      }
      // "Not a live-WS arrival yet" is TRANSIENT, not draft-intrinsic:
      // EntityStore defers its emit to a microtask, so the REST rehydrate
      // can land (and emit) a draft id before `DRAFT_ADDED` records its
      // arrival. Do NOT memoize — a later emission re-evaluates it. (The
      // arrival set still resets on reload, so a REST-only draft never
      // opens; re-checking is a cheap Set.has.)
      return wasDraftWsDelivered(id);
    });
    if (next == null) return;

    const id = String(next.id);
    // Already opened in another tab / a previous mount.
    if (!claimDraftOnce(id)) {
      seenIds.current.add(id);
      return;
    }

    seenIds.current.add(id);
    createMarket({
      rfqId: next.id,
      type: 'RFQ',
      onError: (message) => {
        // The open failed (payload build / draft fetch). Release the
        // claim so a remount or reload can retry instead of burning it.
        releaseDraftClaim(id);
        onError(message);
      },
    });
  }, [drafts, createMarket, isVisible]);

  return null;
}
